import { useInjection } from 'inversify-react';
import type React from 'react';
import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import type { ViewController } from '@/engine/app/View.controller';
import { TYPES } from '@/engine/ioc';
import { Canvas } from '../canvas/Canvas';
import { TwoDLayer } from '../twoDLayer/TwoDLayer';
import type {
  IViewPortManipulationApi,
  ITwoDLayerController,
  IViewPortControllerFactory,
  IViewPortController,
  IContextStateApi
} from '@/engine/app/interfaces';
import { useResetCursorOnPointerLeave } from './useResetCursorOnPointerLeave';

const handleContextLoss = (): void => {
  document.dispatchEvent(new Event('webglcontextlost'));
};

export interface ViewPortProps extends React.HTMLAttributes<HTMLDivElement> {
  descriptor: string;
  bgColor: number;
  viewPortManipulationApi?: IViewPortManipulationApi;
  contextStateApi?: IContextStateApi;
  isMaximized?: boolean;
}

export const ViewPort: React.FC<ViewPortProps> = (props) => {
  const { descriptor, bgColor, viewPortManipulationApi, contextStateApi, isMaximized } = props;
  const [canvasRef, canvasContainerRef, twoDLayerController] = useViewPortController(
    descriptor,
    bgColor,
    viewPortManipulationApi,
    contextStateApi,
    isMaximized
  );

  return (
    <Container ref={canvasContainerRef} data-testid={descriptor + 'ViewPort'} className="canvas-container">
      {twoDLayerController && <TwoDLayer controller={twoDLayerController} isMaximized={isMaximized} />}
      <Canvas ref={canvasRef} />
    </Container>
  );
};

export function useViewPortController(
  descriptor: string,
  bgColor: number,
  viewPortManipulationApi?: IViewPortManipulationApi,
  contextStateApi?: IContextStateApi,
  isMaximized?: boolean
): [React.RefObject<HTMLCanvasElement>, React.RefObject<HTMLDivElement>, ITwoDLayerController | undefined] {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const viewController = useInjection<ViewController>(TYPES.ViewController);
  const viewPortControllerFactory = useInjection<IViewPortControllerFactory>(TYPES.IViewPortControllerFactory);
  const [controller, setController] = useState<IViewPortController>();

  useEffect(() => {
    const canvas = canvasRef.current;
    const canvasContainer = canvasContainerRef.current;
    if (canvas && canvasContainer) {
      const currentVPC = viewController.getViewPortController(descriptor);
      if (currentVPC) {
        viewPortControllerFactory.updateViewPortController(currentVPC, descriptor, canvas, canvasContainer);
        setController(currentVPC);
      } else {
        const vpc = viewPortControllerFactory.createViewPortController(descriptor, canvas, canvasContainer, bgColor);
        setController(vpc);
        viewController.registerViewPort(descriptor, vpc);
      }
      canvas.addEventListener('webglcontextlost', handleContextLoss);
    }
    return (): void => {
      canvas?.removeEventListener('webglcontextlost', handleContextLoss);
      setController(undefined);
    };
    // bgColor intentionally excluded, it triggers updateBackground useEffect instead
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptor, viewController, viewPortControllerFactory]);

  useResetCursorOnPointerLeave(canvasRef, controller);
  useEffect(() => {
    if (isMaximized !== undefined) {
      controller?.setMaximized(isMaximized);
    }
  }, [controller, isMaximized]);

  useEffect(
    function updateBackground() {
      controller?.setBackgroundColor(bgColor);
      controller?.render();
    },
    [bgColor, controller]
  );

  useEffect(() => {
    controller?.setViewPortManipulationApi(viewPortManipulationApi);
  }, [controller, viewPortManipulationApi]);

  useEffect(() => {
    controller?.setContextStateApi(contextStateApi);
  }, [controller, contextStateApi]);

  return [canvasRef, canvasContainerRef, controller?.getTwoDLayerController()];
}

const Container = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  min-height: 1px;
  min-width: 1px;
  touch-action: none;
`;
